import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { ChevronDown } from "lucide-react";
import config from '../config';
import AppAppBar from '../components/AppAppBar';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CommentDialog from './CommentDialog';
import { checkAuthStatus } from '../utils/api';
import { Helmet } from 'react-helmet-async'; // 記得引入 Helmet

const server = config.server;

const StyledTypography = styled(Typography)({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const TitleTypography = styled(Typography)(({ theme }) => ({
  position: 'relative',
  textDecoration: 'none',
  '&:hover': { cursor: 'pointer' },
  '& .arrow': {
    visibility: 'hidden',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '&:hover .arrow': {
    visibility: 'visible',
    opacity: 0.7,
  },
  '&:focus-visible': {
    outline: '3px solid',
    outlineColor: 'hsla(210, 98%, 48%, 0.5)',
    outlineOffset: '3px',
    borderRadius: '8px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 0,
    height: '1px',
    bottom: 0,
    left: 0,
    backgroundColor: (theme.vars || theme).palette.text.primary,
    opacity: 0.3,
    transition: 'width 0.3s ease, opacity 0.3s ease',
  },
  '&:hover::before': {
    width: '100%',
  },
}));

export default function Latest() {
  const [openCommentDialog, setOpenCommentDialog] = React.useState(false);
  const [selectedTweet, setSelectedTweet] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [focusedCardIndex, setFocusedCardIndex] = React.useState(null);

  const handleFocus = (index) => {
    setFocusedCardIndex(index);
  };

  const handleBlur = () => {
    setFocusedCardIndex(null);
  };

  const [tweets, setTweets] = React.useState([]);
  const [isMore, setIsMore] = React.useState(false);

  const fetchTweets = async (url) => {
    const apiUrl = url;
    try {
      const res = await fetch(apiUrl);
      const data = await res.json();
      setTweets(data);
    } catch (error) {
      console.log('Error fetching data', error);
    }
  };

  React.useEffect(() => {
    fetchTweets(server + '/api/tweets/');
    setIsAuthenticated(checkAuthStatus());
  }, []);

  const handleClickMore = () => {
    fetchTweets(server + '/api/more/');
    setIsMore(true);
  };

  const handleCommentClick = (tweet) => {
    setSelectedTweet(tweet);
    setOpenCommentDialog(true);
  };

  const searchTweets = async (query = "") => {
    try {
      const res = await fetch(server + '/api/more/' + (query ? `?q=${encodeURIComponent(query)}` : ""));
      const data = await res.json();
      setTweets(data);
      setIsMore(true);
    } catch (error) {
      console.log('Error fetching data', error);
    }
  };

  function utc2local(utc_str) {
    const utc = new Date(utc_str);
    let offset = utc.getTimezoneOffset();
    let local = new Date(utc.getTime() - offset * 60000);
    return local.toString();
  }

  // 添加全域背景樣式的 useEffect
  React.useEffect(() => {
    // 設定 body 的背景
    const originalBackground = document.body.style.background;
    const originalBackgroundColor = document.body.style.backgroundColor;
    const originalBackgroundImage = document.body.style.backgroundImage;
    const originalBackgroundSize = document.body.style.backgroundSize;
    
    document.body.style.backgroundColor = '#f6f4ea';
    document.body.style.backgroundImage = `
      radial-gradient(1000px 600px at 20% -10%, rgba(255,255,255,0.7), rgba(255,255,255,0) 60%),
      radial-gradient(900px 500px at 120% 0%, rgba(255,255,255,0.55), rgba(255,255,255,0) 65%),
      linear-gradient(180deg, rgba(0,0,0,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
    `;
    document.body.style.backgroundSize = `auto, auto, 100% 28px, 28px 100%`;
    document.body.style.backgroundAttachment = 'scroll';
    document.body.style.minHeight = '100vh';
    
    // 清理函數：組件卸載時恢復原始背景
    return () => {
      document.body.style.background = originalBackground;
      document.body.style.backgroundColor = originalBackgroundColor;
      document.body.style.backgroundImage = originalBackgroundImage;
      document.body.style.backgroundSize = originalBackgroundSize;
      document.body.style.backgroundAttachment = '';
      document.body.style.minHeight = '';
    };
  }, []);

  // 👇 1. 把函數放在組件裡面，這樣它才讀得到 tweets 變數
  const generateJsonLd = () => {
    if (tweets.length === 0) return null;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": tweets.map((tweet, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "NewsArticle",
          "headline": tweet.tweet_text.substring(0, 100).replace(/[\r\n]+/g, " "), // 避免換行符號破壞 JSON
          "datePublished": tweet.created_at,
          "author": {
            "@type": "Organization",
            "name": tweet.agency
          }
        }
      }))
    };

    return (
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* 👇 2. 放在這裡：渲染出來的標籤會自動被 Helmet 抽到網頁的 <head> 裡面 */}
      <Helmet>
        <title>最新全球財經新聞與動態</title>
        <meta name="description" content="即時追蹤全球宏觀經濟、市場動態與機構發文。" />
        {generateJsonLd()}
      </Helmet>

      <AppAppBar onSearch={searchTweets} />
      
      <Box
        sx={{
          width: "100%",
          maxWidth: "900px",
          margin: "0 auto",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(12px)",
          borderRadius: "16px",
          boxShadow: "0 4px 30px rgba(0,0,0,0.1)",
          px: 3,
          py: 4,
          mt: 16.5,
          mb: 4,
        }}
      >
        <Grid
          container
          rowSpacing={1}
          sx={{ alignItems: "center" }}
        >
          {tweets.map((tweet, index) => (
            <Grid key={index} xs={12} component={"article"}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 0,
                  height: "100%",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography gutterBottom variant="body1" color="skyblue">
                    {tweet.agency}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      onClick={() => handleCommentClick(tweet)}
                      startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '999px',
                        color: 'text.primary',
                        fontSize: '0.85rem',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {tweet.comment_count || 0}
                    </Button>
                  </Box>
                </Box>

                <TitleTypography
                  gutterBottom
                  variant="h5"
                  component="h2"
                  onFocus={() => handleFocus(index)}
                  onBlur={handleBlur}
                  tabIndex={0}
                  className={focusedCardIndex === index ? "Mui-focused" : ""}
                  color="gray"
                  sx={{ whiteSpace: "pre-line" }}
                  // onDoubleClick={() => handleCommentClick(tweet)}
                  onClick={() => window.open(`/news/${tweet.tweet_id}`, '_blank')}
                >
                  {tweet.tweet_text.replace(/[\r\n]+/g, " ") + "\n【中文正體】" + tweet.txt_tw}
                </TitleTypography>

                <StyledTypography
                  gutterBottom
                  variant="caption"
                  color="darkgreen"
                  component="time"
                  dateTime={new Date(tweet.created_at).toISOString()}
                >
                  {utc2local(tweet.created_at)}
                </StyledTypography>
              </Box>
            </Grid>
          ))}

          {!isMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, width: "100%" }}>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<ChevronDown size={18} />}
                onClick={handleClickMore}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                  px: 2.5,
                  py: 1,
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderColor: "rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(0,123,255,0.08)",
                    borderColor: "rgba(0,123,255,0.3)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                查看更多
              </Button>
            </Box>
          )}
        </Grid>

        {selectedTweet && (
          <CommentDialog
            open={openCommentDialog}
            onClose={() => setOpenCommentDialog(false)}
            tweet={selectedTweet}
            isAuthenticated={isAuthenticated}
          />
        )}
      </Box>
    </Box>
  );
}